/* Vector operations — run the vector rollout steps (cleanup, taxonomy load,
   re-embed, re-sync, audit, shadow) from the dashboard instead of a server
   console. Each step previews first; applying asks for confirmation. Runs
   execute on the server's worker, so this page only starts, watches and
   cancels them. */

import React, { useEffect, useMemo, useState } from 'react';
import { Badge } from 'src/ui/Badge';
import type { BadgeColor } from 'src/ui/Badge';
import { Button } from 'src/ui/Button';
import { Card } from 'src/ui/Card';
import { Input } from 'src/ui/Input';
import { Select } from 'src/ui/Select';
import { Modal } from 'src/ui/Modal';
import { Alert } from 'src/ui/Alert';
import { EmptyState } from 'src/ui/EmptyState';
import { LoadingBlock } from 'src/ui/Spinner';
import { useToast } from 'src/ui/Toast';
import {
  VectorOpDefinition,
  VectorOpId,
  VectorOpRun,
  VectorOpStatus,
  useCancelVectorOpMutation,
  useGetVectorOpRunQuery,
  useGetVectorOpsQuery,
  useResumeVectorOpMutation,
  useStartVectorOpMutation,
} from 'src/store/vectorProfileApi';

const POLL_MS = 3000;

const isActive = (r: VectorOpRun) =>
  r.status === 'queued' || r.status === 'running';

const STATUS: Record<VectorOpStatus, [BadgeColor, string]> = {
  queued: ['gray', 'Queued'],
  running: ['blue', 'Running'],
  succeeded: ['green', 'Done'],
  failed: ['red', 'Failed'],
  cancelled: ['amber', 'Cancelled'],
};

function RunStatus({ run }: { run: VectorOpRun }) {
  if (run.stalled) {
    return (
      <Badge color="amber" size="sm" dot>
        Stalled
      </Badge>
    );
  }
  const [color, label] = STATUS[run.status] ?? ['gray', run.status];
  return (
    <Badge color={color} size="sm" dot>
      {run.cancelRequested && isActive(run) ? 'Cancelling' : label}
    </Badge>
  );
}

/** "Preview" / "Applied" for data-changing steps, "Ran" for read-only ones. */
const modeLabel = (run: VectorOpRun, writes: boolean) =>
  writes ? (run.dryRun ? 'Preview' : 'Applied') : 'Ran';

const fmtTime = (d: string | null) => (d ? new Date(d).toLocaleString() : '—');

function fmtDuration(run: VectorOpRun): string {
  if (!run.startedAt) return '—';
  const end = run.finishedAt ? new Date(run.finishedAt) : new Date();
  const s = Math.max(
    0,
    Math.round((end.getTime() - new Date(run.startedAt).getTime()) / 1000)
  );
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

function Progress({ run }: { run: VectorOpRun }) {
  if (!run.progressTotal) {
    return isActive(run) ? (
      <span className="text-muted" style={{ fontSize: 'var(--fs-xs)' }}>
        Working…
      </span>
    ) : null;
  }
  const pct = Math.min(
    100,
    Math.round((run.progressDone / run.progressTotal) * 100)
  );
  return (
    <div className="col" style={{ gap: 4, minWidth: 160 }}>
      <div
        style={{
          height: 6,
          borderRadius: 'var(--radius-full)',
          background: 'var(--bg-muted)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: '100%',
            background: 'var(--accent)',
          }}
        />
      </div>
      <span className="text-muted" style={{ fontSize: 'var(--fs-2xs)' }}>
        {run.progressDone.toLocaleString()} /{' '}
        {run.progressTotal.toLocaleString()} ({pct}%)
      </span>
    </div>
  );
}

// ---- small table -----------------------------------------------------------

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px 12px',
  fontSize: 'var(--fs-2xs)',
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: 'var(--text-muted)',
  borderBottom: '1px solid var(--border)',
  background: 'var(--bg-subtle)',
};
const tdStyle: React.CSSProperties = {
  padding: '8px 12px',
  borderBottom: '1px solid var(--border)',
  fontSize: 'var(--fs-sm)',
  verticalAlign: 'top',
};

function SimpleTable({
  columns,
  rows,
  empty = 'Nothing to show.',
}: {
  columns: string[];
  rows: React.ReactNode[][];
  empty?: string;
}) {
  if (!rows.length) {
    return (
      <div className="text-muted" style={{ fontSize: 'var(--fs-sm)' }}>
        {empty}
      </div>
    );
  }
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c} style={thStyle}>
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              {r.map((cell, j) => (
                <td key={j} style={tdStyle}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Counts({ items }: { items: [string, React.ReactNode][] }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
        gap: 8,
      }}
    >
      {items.map(([label, value]) => (
        <Card key={label} pad flat>
          <div className="text-muted" style={{ fontSize: 'var(--fs-2xs)' }}>
            {label}
          </div>
          <div
            style={{
              fontSize: 'var(--fs-lg)',
              fontWeight: 600,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {value}
          </div>
        </Card>
      ))}
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="col" style={{ gap: 8 }}>
      <div style={{ fontWeight: 600, fontSize: 'var(--fs-sm)' }}>{title}</div>
      {children}
    </div>
  );
}

const mono = (s: React.ReactNode) => <span className="mono">{s}</span>;
const num = (n: unknown) =>
  typeof n === 'number' ? n.toLocaleString() : String(n ?? '—');

// ---- per-step result views -------------------------------------------------

// Old kinds stay so audit runs stored before the "unknown ≠ no" scorer
// still render: back then a never-met dealbreaker removed every candidate.
const AUDIT_EFFECT: Record<string, [BadgeColor, string]> = {
  neverMet: ['gray', 'Never met (skipped)'],
  neverExcludes: ['amber', 'Excludes nobody'],
  excludesEveryone: ['red', 'Excludes everyone'],
  neverCredited: ['gray', 'Credits nobody'],
};

const WORLD_EFFECT: Record<string, [BadgeColor, string]> = {
  closed: ['red', 'Narrows the pool'],
  single: ['gray', 'Silence = unknown'],
  open: ['gray', 'Silence = unknown'],
};

function AuditResult({ r }: { r: any }) {
  const lockouts = (r.keys ?? []).filter(
    (k: any) => k.kind === 'excludesEveryone' && k.severity === 'DEALBREAKER'
  );
  const lowCoverage: any[] = r.lowCoverage ?? [];
  const narrowing = lowCoverage.filter((k) => k.world === 'closed');
  return (
    <>
      <Counts
        items={[
          ['Profiles scanned', num(r.usersScanned)],
          ['With a dead requirement', num(r.usersWithInertConstraints)],
          ['Distinct fact keys', num(r.factUniverseSize)],
          ['Dealbreaker lockouts', num(lockouts.length)],
          ['Rare strong requirements', num(lowCoverage.length)],
        ]}
      />
      {lockouts.length > 0 && (
        <Alert color="red" title="Some users would get no matches">
          A dealbreaker nobody can meet removes every candidate under the
          severity ladder. Fix these keys (seed merge or re-sync) first.
        </Alert>
      )}
      <Section title="Requirements that cannot take effect">
        <SimpleTable
          columns={['Severity', 'Key', 'Users', 'Effect']}
          empty="Every requirement is reachable."
          rows={(r.keys ?? []).map((k: any) => {
            const [color, label] = AUDIT_EFFECT[k.kind] ?? ['gray', k.kind];
            return [
              k.severity,
              mono(k.key),
              num(k.affectedUsers),
              <Badge color={color} size="sm">
                {label}
              </Badge>,
            ];
          })}
        />
      </Section>
      {r.lowCoverage && (
        <Section title="Strong requirements on rare facts">
          {narrowing.length > 0 && (
            <Alert color="amber" title="Some requirements still narrow pools">
              These come from profile columns, so a candidate without the fact
              really does not have it. Check they are what the users meant.
            </Alert>
          )}
          <SimpleTable
            columns={['Severity', 'Key', 'Users', 'Carriers', 'Effect']}
            empty="No strong requirement is on a rare fact."
            rows={lowCoverage.map((k) => {
              const [color, label] = WORLD_EFFECT[k.world] ?? ['gray', k.world];
              return [
                k.severity,
                mono(k.key),
                num(k.affectedUsers),
                num(k.carriers),
                <Badge color={color} size="sm">
                  {label}
                </Badge>,
              ];
            })}
          />
        </Section>
      )}
    </>
  );
}

function CleanupResult({ r }: { r: any }) {
  const c = r.counts ?? {};
  return (
    <>
      <Counts
        items={[
          ['Retired rows', num(c.retiredRows)],
          ['Retired keys', num(c.retiredKeys)],
          ['Definitions deactivated', num(c.definitionsDeactivated)],
          ['Spellings canonicalized', num(c.keysCanonicalized)],
          ['Rows moved to merged keys', num(c.mergedRowsMoved)],
          ['Contradicted lifestyle facts', num(c.lifestyleDeleted)],
          ['Unsupported children facts', num(c.childrenDeleted)],
          ['Marital labels to review', num(c.suspectMarital)],
          [
            'Religion fixed / unmapped',
            `${num(c.religionFixed)} / ${num(c.religionUnmapped)}`,
          ],
          ['Legacy criteria converted', num(c.criteriaConverted)],
        ]}
      />
      <Section title="Rows deleted on retired keys">
        <SimpleTable
          columns={['Key', 'Type', 'Rows']}
          rows={(r.retiredRowsDeleted ?? []).map((x: any) => [
            mono(x.key),
            x.type,
            num(x.count),
          ])}
        />
      </Section>
      <Section title="Spelling variants folded">
        <SimpleTable
          columns={['From', 'To', 'Mode', 'Rows']}
          rows={(r.keysCanonicalized ?? []).map((x: any) => [
            mono(x.from),
            mono(x.to),
            x.mode,
            num(x.rows),
          ])}
        />
      </Section>
      {(r.mergedRowsMoved ?? []).length > 0 && (
        <Section title="Rows moved off merged keys">
          <SimpleTable
            columns={['From', 'To', 'Rows']}
            rows={r.mergedRowsMoved.map((x: any) => [
              mono(x.from),
              mono(x.to),
              num(x.rows),
            ])}
          />
        </Section>
      )}
      {r.religionUnmapped?.count > 0 && (
        <Section title="Religion values with no mapping (left as is)">
          <SimpleTable
            columns={['User', 'Value']}
            rows={r.religionUnmapped.items.map((x: any) => [
              mono(x.userId),
              x.value,
            ])}
          />
        </Section>
      )}
    </>
  );
}

function LoadTaxonomyResult({ r, dryRun }: { r: any; dryRun: boolean }) {
  if (!dryRun) {
    return (
      <Counts
        items={[
          ['Concepts upserted', num(r.upserted)],
          ['Label rows moved', num(r.remapped)],
          ['Keys retired', num(r.retired)],
          ['Orphan risk', num(r.orphanRisk)],
          ['Orphans remaining', num(r.orphansRemaining)],
        ]}
      />
    );
  }
  const c = r.counts ?? {};
  return (
    <>
      <Counts
        items={[
          ['Concepts upserted', num(c.upserts)],
          ['Rows to move', num(c.rowsToMove)],
          [
            'Merges with rows',
            `${num(c.remapsWithRows)} of ${num(c.remapRules)}`,
          ],
          ['Keys retired', num(c.retires)],
          ['Promoted / created', `${num(c.promoted)} / ${num(c.created)}`],
          ['Reactivated', num(c.reactivated)],
          ['Skipped conflicts', num(c.conflicts)],
          ['Orphan risk', num(c.orphanRisk)],
        ]}
      />
      {c.reactivated > 0 && (
        <Alert color="amber" title="Merged keys would become active again">
          Check these before applying:{' '}
          {r.reactivated.map((x: any) => x.key).join(', ')}
        </Alert>
      )}
      <Section title="Label rows that move">
        <SimpleTable
          columns={['From', 'Into', 'Rows']}
          empty="No label rows move."
          rows={(r.remaps ?? []).map((x: any) => [
            mono(x.fromKey),
            mono(x.toKey),
            num(x.rows),
          ])}
        />
      </Section>
      {(r.promoted?.length > 0 || r.created?.length > 0) && (
        <Section title="Concepts promoted (pending → active) and created">
          <div style={{ fontSize: 'var(--fs-sm)' }}>
            {[...(r.promoted ?? []), ...(r.created ?? [])].map((k: string) => (
              <Badge key={k} color="gray" size="sm">
                {k}
              </Badge>
            ))}
          </div>
        </Section>
      )}
      {(r.conflicts ?? []).length > 0 && (
        <Section title="Seed rules skipped (they contradict each other)">
          <SimpleTable
            columns={['Key', 'Merged into', 'Why skipped']}
            rows={r.conflicts.map((x: any) => [
              mono(x.from),
              mono(x.into.join(' | ')),
              x.reason,
            ])}
          />
        </Section>
      )}
    </>
  );
}

function MigrateResult({ r, dryRun }: { r: any; dryRun: boolean }) {
  if (dryRun) {
    return (
      <Counts
        items={[
          ['Users to re-sync', num(r.users)],
          ['Estimated time', `~${num(r.estimateMinutes)} min`],
          ['Scope', r.scope],
        ]}
      />
    );
  }
  return (
    <>
      <Counts
        items={[
          ['Processed', `${num(r.processed)} / ${num(r.total)}`],
          ['Succeeded', num(r.succeeded)],
          ['Failed', num(r.failed)],
        ]}
      />
      {r.failed > 0 && (
        <Section title="Users that failed (re-sync them from their profile)">
          <SimpleTable
            columns={['User']}
            rows={(r.failedUsers ?? []).map((u: string) => [mono(u)])}
          />
        </Section>
      )}
    </>
  );
}

function ShadowResult({ r }: { r: any }) {
  const s = r.summary ?? {};
  return (
    <>
      <Counts
        items={[
          ['Seekers compared', num(s.seekers)],
          ['Top match changed', num(s.seekersWithChangedTop)],
          [
            'Gaining candidates',
            `${num(s.seekersWithRecovered)} (${num(s.totalRecovered)})`,
          ],
          [
            'Losing candidates',
            `${num(s.seekersWithNewExclusions)} (${num(s.totalNewlyExcluded)})`,
          ],
          ['Empty pools fixed', num(s.emptyPoolsFixed)],
          ['Empty pools created', num(s.emptyPoolsCreated)],
          [
            'Mean rank correlation',
            s.meanSpearman == null
              ? 'n/a'
              : `${Number(s.meanSpearman).toFixed(2)} (n=${s.spearmanSeekers})`,
          ],
        ]}
      />
      <Section title="Per seeker (current engine → ladder)">
        <SimpleTable
          columns={['Seeker', 'Pool', 'Shown', 'Gained', 'Lost', 'Top', 'ρ']}
          rows={(r.seekers ?? []).map((x: any) => [
            mono(x.userId),
            num(x.poolSize),
            `${x.v1Shown} → ${x.v2Shown}`,
            num(x.recovered),
            num(x.newlyExcluded),
            x.topChanged ? 'changed' : 'same',
            x.spearman == null ? '—' : Number(x.spearman).toFixed(2),
          ])}
        />
      </Section>
    </>
  );
}

function RunResult({ run }: { run: VectorOpRun }) {
  const r = run.result;
  if (!r) return null;
  switch (run.op) {
    case 'audit-constraints':
      return <AuditResult r={r} />;
    case 'cleanup-labels':
      return <CleanupResult r={r} />;
    case 'load-taxonomy':
      return <LoadTaxonomyResult r={r} dryRun={run.dryRun} />;
    case 'migrate':
      return <MigrateResult r={r} dryRun={run.dryRun} />;
    case 'shadow':
      return <ShadowResult r={r} />;
    case 'reembed-labels':
      return (
        <Counts
          items={[
            ['Labels with a vector', num(r.total)],
            ['Re-embedded', num(r.reembedded)],
          ]}
        />
      );
    default:
      return (
        <pre className="mono" style={{ fontSize: 'var(--fs-xs)' }}>
          {JSON.stringify(r, null, 2)}
        </pre>
      );
  }
}

// ---- run detail modal ------------------------------------------------------

function RunModal({
  runId,
  title,
  writes,
  onClose,
}: {
  runId: string;
  title: string;
  writes: boolean;
  onClose: () => void;
}) {
  // Poll until the run finishes, then stop.
  const [finished, setFinished] = useState(false);
  const { data: run, isLoading } = useGetVectorOpRunQuery(runId, {
    pollingInterval: finished ? 0 : POLL_MS,
  });
  useEffect(() => setFinished(!!run && !isActive(run)), [run]);
  return (
    <Modal
      open
      onClose={onClose}
      width={960}
      title={`${title}${writes && run?.dryRun ? ' — preview' : ''}`}
      subtitle={
        run
          ? `Started ${fmtTime(run.startedAt ?? run.createdAt)} by ${
              run.startedBy ?? 'unknown'
            } · ${fmtDuration(run)}`
          : undefined
      }
      footer={<Button onClick={onClose}>Close</Button>}
    >
      {isLoading || !run ? (
        <LoadingBlock label="Loading run…" />
      ) : (
        <div className="col" style={{ gap: 16 }}>
          <div className="row" style={{ gap: 12, alignItems: 'center' }}>
            <RunStatus run={run} />
            <Progress run={run} />
          </div>
          {run.error && (
            <Alert color="red" title="The step stopped with an error">
              {run.error}
            </Alert>
          )}
          {isActive(run) && !run.result && (
            <div className="text-muted" style={{ fontSize: 'var(--fs-sm)' }}>
              Results appear here when the step finishes. You can close this
              window; the step keeps running on the server.
            </div>
          )}
          <RunResult run={run} />
        </div>
      )}
    </Modal>
  );
}

// ---- one step card ---------------------------------------------------------

function StepParams({
  op,
  params,
  setParams,
}: {
  op: VectorOpDefinition;
  params: Record<string, unknown>;
  setParams: (p: Record<string, unknown>) => void;
}) {
  if (op.id === 'migrate') {
    return (
      <div className="row" style={{ gap: 8, alignItems: 'center' }}>
        <Select
          size="sm"
          style={{ maxWidth: 340 }}
          value={String(params.scope ?? 'all')}
          onChange={(e) => setParams({ ...params, scope: e.target.value })}
          aria-label="Which profiles"
        >
          <option value="all">All profiles</option>
          <option value="missing">
            Only profiles missing a vector or labels
          </option>
          <option value="feedback">Only profiles with unsynced feedback</option>
        </Select>
        <label
          className="row text-muted"
          style={{ gap: 6, alignItems: 'center', fontSize: 'var(--fs-xs)' }}
        >
          Pause between users (ms)
          <Input
            size="sm"
            type="number"
            min={0}
            max={10000}
            style={{ width: 90 }}
            value={String(params.delayMs ?? 500)}
            onChange={(e) =>
              setParams({ ...params, delayMs: Number(e.target.value) })
            }
          />
        </label>
      </div>
    );
  }
  if (op.id === 'shadow') {
    return (
      <label
        className="row text-muted"
        style={{ gap: 6, alignItems: 'center', fontSize: 'var(--fs-xs)' }}
      >
        Seekers to compare
        <Input
          size="sm"
          type="number"
          min={1}
          max={500}
          style={{ width: 80 }}
          value={String(params.sample ?? 50)}
          onChange={(e) =>
            setParams({ ...params, sample: Number(e.target.value) })
          }
        />
      </label>
    );
  }
  return null;
}

function StepCard({
  op,
  latest,
  latestPreview,
  writeBusy,
  onOpenRun,
}: {
  op: VectorOpDefinition;
  latest?: VectorOpRun;
  latestPreview?: VectorOpRun;
  writeBusy?: VectorOpRun;
  onOpenRun: (run: VectorOpRun) => void;
}) {
  const toast = useToast();
  const [params, setParams] = useState<Record<string, unknown>>({});
  const [confirming, setConfirming] = useState(false);
  const [start, startState] = useStartVectorOpMutation();
  const [cancel, cancelState] = useCancelVectorOpMutation();
  const [resume, resumeState] = useResumeVectorOpMutation();
  const busy = !!latest && isActive(latest) && !latest.stalled;

  const run = async (dryRun: boolean) => {
    try {
      const created = await start({ op: op.id, dryRun, params }).unwrap();
      setConfirming(false);
      onOpenRun(created);
    } catch (e: any) {
      toast({
        type: 'error',
        title: `Could not start “${op.title}”`,
        message: e?.data?.message ?? 'Request failed',
      });
    }
  };

  const canResume =
    !!latest &&
    (latest.stalled ||
      (latest.status === 'failed' &&
        latest.op === 'migrate' &&
        !latest.dryRun));

  return (
    <Card pad>
      <div className="col" style={{ gap: 10 }}>
        <div
          className="row"
          style={{ justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}
        >
          <div className="col" style={{ gap: 4, minWidth: 0, flex: 1 }}>
            <div className="row" style={{ gap: 8, alignItems: 'center' }}>
              <span className="text-muted" style={{ fontSize: 'var(--fs-xs)' }}>
                Step {op.step}
              </span>
              <span style={{ fontWeight: 600 }}>{op.title}</span>
              <Badge color={op.writes ? 'purple' : 'gray'} size="sm">
                {op.writes ? 'Changes data' : 'Read-only'}
              </Badge>
              {op.usesAi && op.writes && (
                <Badge color="amber" size="sm">
                  Uses AI
                </Badge>
              )}
            </div>
            <div
              className="text-secondary"
              style={{ fontSize: 'var(--fs-sm)', maxWidth: 720 }}
            >
              {op.description}
            </div>
            <div
              className="text-faint mono"
              style={{ fontSize: 'var(--fs-2xs)' }}
            >
              CLI: {op.cli}
            </div>
          </div>
          <div className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
            {op.writes ? (
              <>
                <Button
                  variant="secondary"
                  leftIcon="eye"
                  disabled={busy}
                  loading={startState.isLoading && !confirming}
                  onClick={() => run(true)}
                >
                  Preview
                </Button>
                <Button
                  variant="primary"
                  leftIcon="play"
                  disabled={busy || !!writeBusy}
                  onClick={() => setConfirming(true)}
                >
                  Apply…
                </Button>
              </>
            ) : (
              <Button
                variant="primary"
                leftIcon="play"
                disabled={busy}
                loading={startState.isLoading}
                onClick={() => run(true)}
              >
                Run
              </Button>
            )}
          </div>
        </div>

        <StepParams op={op} params={params} setParams={setParams} />

        {latest && (
          <div
            className="row"
            style={{
              gap: 12,
              alignItems: 'center',
              flexWrap: 'wrap',
              paddingTop: 8,
              borderTop: '1px solid var(--border)',
              fontSize: 'var(--fs-xs)',
            }}
          >
            <span className="text-muted">Last run</span>
            <RunStatus run={latest} />
            <span className="text-muted">
              {modeLabel(latest, op.writes)} ·{' '}
              {fmtTime(latest.startedAt ?? latest.createdAt)} ·{' '}
              {latest.startedBy ?? 'unknown'} · {fmtDuration(latest)}
            </span>
            <Progress run={latest} />
            <div className="row" style={{ gap: 6, marginLeft: 'auto' }}>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onOpenRun(latest)}
              >
                View result
              </Button>
              {isActive(latest) && !latest.cancelRequested && (
                <Button
                  size="sm"
                  variant="danger-ghost"
                  loading={cancelState.isLoading}
                  onClick={() => cancel(latest.id)}
                >
                  Cancel
                </Button>
              )}
              {canResume && (
                <Button
                  size="sm"
                  variant="secondary"
                  leftIcon="rotate-cw"
                  loading={resumeState.isLoading}
                  onClick={() =>
                    resume(latest.id)
                      .unwrap()
                      .catch((e: any) =>
                        toast({
                          type: 'error',
                          title: 'Could not resume',
                          message: e?.data?.message ?? 'Request failed',
                        })
                      )
                  }
                >
                  Resume
                </Button>
              )}
            </div>
          </div>
        )}
        {latest?.error && !isActive(latest) && (
          <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--red-text)' }}>
            {latest.error}
          </div>
        )}
      </div>

      <Modal
        open={confirming}
        onClose={startState.isLoading ? undefined : () => setConfirming(false)}
        title={`Apply “${op.title}”?`}
        subtitle="This changes data on this environment."
        width={560}
        footer={
          <>
            <Button
              variant="ghost"
              disabled={startState.isLoading}
              onClick={() => setConfirming(false)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              leftIcon="play"
              loading={startState.isLoading}
              onClick={() => run(false)}
            >
              Apply
            </Button>
          </>
        }
      >
        <div className="col" style={{ gap: 12, fontSize: 'var(--fs-sm)' }}>
          <div>{op.description}</div>
          {latestPreview?.status === 'succeeded' ? (
            <Alert color="blue" title="Latest preview">
              {fmtTime(latestPreview.finishedAt)} by{' '}
              {latestPreview.startedBy ?? 'unknown'}.{' '}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setConfirming(false);
                  onOpenRun(latestPreview);
                }}
              >
                Review it
              </Button>
            </Alert>
          ) : (
            <Alert color="amber" title="No preview yet">
              Run Preview first to see exactly what this step will change.
            </Alert>
          )}
          {op.usesAi && (
            <div className="text-muted">
              This step calls the AI provider. Make sure the environment's
              provider key has credits.
            </div>
          )}
          {writeBusy && (
            <Alert color="amber" title="Another step is changing data">
              Wait for it to finish.
            </Alert>
          )}
        </div>
      </Modal>
    </Card>
  );
}

// ---- page panel ------------------------------------------------------------

export function VectorOperations() {
  const [polling, setPolling] = useState(false);
  const { data, isLoading, isError } = useGetVectorOpsQuery(undefined, {
    pollingInterval: polling ? POLL_MS : 0,
  });
  const [openRun, setOpenRun] = useState<VectorOpRun | null>(null);

  const runs = useMemo(() => data?.runs ?? [], [data]);
  // Poll only while something runs; an idle page makes no requests.
  const anyActive = runs.some((r) => isActive(r) && !r.stalled);
  useEffect(() => setPolling(anyActive), [anyActive]);

  const ops = useMemo(
    () => [...(data?.ops ?? [])].sort((a, b) => a.step - b.step),
    [data]
  );
  const titles = useMemo(
    () =>
      Object.fromEntries(ops.map((o) => [o.id, o.title])) as Partial<
        Record<VectorOpId, string>
      >,
    [ops]
  );

  const writeBusy = runs.find(
    (r) =>
      isActive(r) &&
      !r.stalled &&
      !r.dryRun &&
      ops.find((o) => o.id === r.op)?.writes
  );

  return (
    <div className="col" style={{ height: '100%', overflow: 'hidden' }}>
      <div
        style={{
          padding: '16px 22px',
          borderBottom: '1px solid var(--border)',
          flexShrink: 0,
          background: 'var(--bg-canvas)',
        }}
      >
        <h1 className="page-title" style={{ fontSize: 'var(--fs-2xl)' }}>
          Vector Operations
        </h1>
        <div className="text-muted" style={{ fontSize: 'var(--fs-sm)' }}>
          The vector rollout steps, in order. Preview a step to see what it will
          change, then apply it. Steps run on the server, so you can leave this
          page.
        </div>
      </div>

      <div
        className="col scroll-y grow"
        style={{ padding: '16px 22px', gap: 12, minHeight: 0 }}
      >
        {isLoading ? (
          <LoadingBlock label="Loading operations…" />
        ) : isError || !data ? (
          <EmptyState
            icon="alert-circle"
            title="Could not load operations"
            desc="The server may not have this feature yet, or you may not be an admin."
          />
        ) : (
          <>
            {ops.map((op) => {
              const mine = runs.filter((r) => r.op === op.id);
              return (
                <StepCard
                  key={op.id}
                  op={op}
                  latest={mine[0]}
                  latestPreview={mine.find((r) => r.dryRun)}
                  writeBusy={writeBusy}
                  onOpenRun={setOpenRun}
                />
              );
            })}

            <div style={{ fontWeight: 600, marginTop: 12 }}>Run history</div>
            <Card>
              <SimpleTable
                columns={[
                  'Started',
                  'Step',
                  'Mode',
                  'Status',
                  'Progress',
                  'By',
                  'Took',
                  '',
                ]}
                empty="No runs yet."
                rows={runs.map((r) => [
                  fmtTime(r.startedAt ?? r.createdAt),
                  titles[r.op] ?? r.op,
                  modeLabel(r, !!ops.find((o) => o.id === r.op)?.writes),
                  <RunStatus run={r} />,
                  r.progressTotal
                    ? `${num(r.progressDone)} / ${num(r.progressTotal)}`
                    : '—',
                  r.startedBy ?? '—',
                  fmtDuration(r),
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setOpenRun(r)}
                  >
                    View
                  </Button>,
                ])}
              />
            </Card>
          </>
        )}
      </div>

      {openRun && (
        <RunModal
          runId={openRun.id}
          title={titles[openRun.op] ?? openRun.op}
          writes={!!ops.find((o) => o.id === openRun.op)?.writes}
          onClose={() => setOpenRun(null)}
        />
      )}
    </div>
  );
}

export default VectorOperations;
